/**
 * The floating toolbars of the web editors (plan 7.3, 7.4, 7.8):
 *
 * - `SelectionToolbar`, over selected text: Ask Tabbio, Shorter, Longer,
 *   Tone (More casual, More formal) and Suggest edits.
 * - `FloatingBar`, the positioning both it and the element bar use: centred
 *   over the target's rect (in the stage's coordinates), above it when there
 *   is room, else below, kept inside the stage.
 *
 * Buttons keep the selection in the page (mouse down does not take focus);
 * the bars are keyboard reachable and Escape hands focus back to the page.
 * The layout direction mirrors them in RTL.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { WebMenu, WebMenuItem } from '../../web/WebMenu';
import {
  SELECTION_TOOLBAR_ITEMS,
  selectionToolbarPlacement,
  type SelectionAction,
} from './selection-toolbar-model';

export type StageRect = { x: number; y: number; width: number; height: number };

const ICONS: Record<SelectionAction, string> = {
  ask: 'auto_awesome',
  shorter: 'short_text',
  longer: 'notes',
  casual: 'sentiment_satisfied',
  formal: 'work',
  suggest: 'lightbulb',
};

export function FloatingBar({
  rect,
  stage,
  label,
  children,
  onEscape,
  className,
}: {
  rect: StageRect;
  stage: { width: number; height: number };
  label: string;
  children: ReactNode;
  onEscape?: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 320, height: 40 });
  // Measured after every render (its buttons change with the target); the
  // equality check below ends the loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const next = { width: node.offsetWidth, height: node.offsetHeight };
    setSize((current) => (current.width === next.width && current.height === next.height ? current : next));
  });
  const place = selectionToolbarPlacement({ rect, container: stage, toolbar: size });
  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={label}
      className={`tabbio-floating-bar${className ? ` ${className}` : ''}`}
      style={{ left: place.left, top: place.top }}
      onMouseDown={(event) => {
        // Keep the selection and the caret in the page.
        if ((event.target as HTMLElement).closest('button')) event.preventDefault();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && onEscape) {
          event.stopPropagation();
          onEscape();
        }
      }}
    >
      {children}
    </div>
  );
}

export function SelectionToolbar({
  rect,
  stage,
  onAction,
  onEscape,
}: {
  rect: StageRect;
  stage: { width: number; height: number };
  onAction: (action: SelectionAction) => void;
  onEscape?: () => void;
}) {
  const { t } = useTranslation('common');
  const [toneOpen, setToneOpen] = useState(false);
  const toneRef = useRef<HTMLButtonElement | null>(null);
  return (
    <FloatingBar rect={rect} stage={stage} label={t('chat.artifacts.toolbar.label')} onEscape={onEscape} className="is-selection">
      {SELECTION_TOOLBAR_ITEMS.map((item) =>
        item.kind === 'action' ? (
          <button
            key={item.action}
            type="button"
            className={`tabbio-floating-button${item.action === 'ask' ? ' is-primary' : ''}`}
            onClick={() => onAction(item.action)}
          >
            <MaterialSymbol name={ICONS[item.action]} size={16} />
            <span>{t(item.label)}</span>
          </button>
        ) : (
          <span key={item.label} className="tabbio-floating-menu-anchor">
            <button
              ref={toneRef}
              type="button"
              className="tabbio-floating-button"
              aria-haspopup="menu"
              aria-expanded={toneOpen}
              onClick={() => setToneOpen((value) => !value)}
            >
              <MaterialSymbol name="tune" size={16} />
              <span>{t(item.label)}</span>
              <MaterialSymbol name="expand_more" size={16} />
            </button>
            <WebMenu open={toneOpen} onClose={() => setToneOpen(false)} anchor={toneRef.current} placement="bottom-start" minWidth={180} style={{ borderRadius: 12 }}>
              {item.actions.map((entry) => (
                <WebMenuItem
                  key={entry.action}
                  icon={ICONS[entry.action]}
                  label={t(entry.label)}
                  onSelect={() => onAction(entry.action)}
                />
              ))}
            </WebMenu>
          </span>
        ),
      )}
    </FloatingBar>
  );
}
